import { Game } from "../../../services/gameQuiz/gameQuiz.types";

/**
 * Read-only answer formatter for the drill-down modal.
 *
 * `answers` is stored as schemaless JSON, so the shape per game type has to
 * come from somewhere. The authority is the SDK scorer,
 * `emmi-quiz-engine/packages/core/src/core/score.ts` — the same code the player
 * runs to grade the attempt, documented in its "Answer shape conventions per
 * game type" block. Read it before touching a case here.
 *
 * Do NOT infer a shape from how this file already behaves. An earlier revision
 * guessed three of them (image_labeling keys/values swapped, fill_in_blanks and
 * drag_and_drop keyed by id instead of index) and the tests asserted the guess,
 * so the suite stayed green while the modal rendered raw ids to operators.
 *
 * Unrecognized values still fall back to pretty-printed JSON, and unresolvable
 * ids stay visible as ids: showing the operator a raw id is bad, silently
 * dropping the student's answer is worse.
 */

const UNANSWERED = "Sin responder";
/** Slot the student reached but left without an answer. */
const EMPTY_SLOT = "(vacío)";

const stringify = (value: unknown): string => {
  if (value === undefined || value === null) return UNANSWERED;
  if (typeof value === "string") return value.trim() ? value : UNANSWERED;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const isEmpty = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "") ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === "object" && Object.keys(value as object).length === 0);

// Cheap id → label maps used by all option/item/hotspot-style game types.
type Labelable = { id: string; text?: string; label?: string; name?: string };
const labelOf = (entries: Labelable[] | undefined, id: string): string => {
  const found = entries?.find((e) => e.id === id);
  return found?.text ?? found?.label ?? found?.name ?? id;
};

export const formatAnswer = (game: Game, answer: unknown): string => {
  if (isEmpty(answer)) return UNANSWERED;

  switch (game.type) {
    case "multiple_choice": {
      // Either a single option id or an array of them (some quizzes allow
      // multi-select on the wire even if the UI is radio-only).
      const ids = Array.isArray(answer) ? answer : [answer];
      return ids
        .map((id) => labelOf(game.options, String(id)))
        .join(", ");
    }

    case "true_false": {
      // Map de statementId -> categoryId.
      if (typeof answer !== "object" || Array.isArray(answer))
        return stringify(answer);
      const entries = Object.entries(answer as Record<string, string>);
      return entries
        .map(([statementId, categoryId]) => {
          const statement = game.statements.find((s) => s.id === statementId);
          const category = game.categories.find((c) => c.id === categoryId);
          return `${statement?.text ?? statementId} → ${category?.label ?? categoryId}`;
        })
        .join("\n");
    }

    case "drag_and_drop":
    case "drag_and_drop_horizontal": {
      // Array de ÍNDICES de item en el orden elegido por el alumno: el scorer
      // lee `answer[pos] === pos` para la identidad (score.ts:97). Un `null` es
      // una casilla que quedó vacía. Filas viejas guardaron itemIds (string):
      // se resuelven igual para no perder el dato.
      if (!Array.isArray(answer)) return stringify(answer);
      return (answer as (number | string | null)[])
        .map((entry, pos) => {
          if (entry === null || entry === undefined)
            return `${pos + 1}. ${EMPTY_SLOT}`;
          const item =
            typeof entry === "number"
              ? game.items[entry]
              : game.items.find((i) => i.id === entry);
          return `${pos + 1}. ${item?.text ?? String(entry)}`;
        })
        .join("\n");
    }

    case "fill_in_blanks": {
      // Map ÍNDICE del hueco -> optionId: el scorer lee `answer[idx]`
      // (score.ts:302), no `answer[blank.id]`. Se recorren todos los huecos —
      // no solo los presentes en el JSON — para que un hueco sin responder se
      // vea como tal en vez de desaparecer de la lista.
      if (typeof answer !== "object" || Array.isArray(answer))
        return stringify(answer);
      const record = answer as Record<string, string | null>;
      return game.blanks
        .map((blank, idx) => {
          // Índice primero (canónico); el id cubre filas legacy.
          const optionId = record[String(idx)] ?? record[blank.id];
          if (!optionId) return `Hueco ${idx + 1}: ${EMPTY_SLOT}`;
          return `Hueco ${idx + 1}: ${labelOf(game.options, optionId)}`;
        })
        .join("\n");
    }

    case "categorization": {
      if (typeof answer !== "object" || Array.isArray(answer))
        return stringify(answer);
      const items = [
        ...game.items,
        ...(game.distractors ?? []),
      ];
      const record = answer as Record<string, unknown>;

      // Wire shape del SDK: categoryId -> itemIds[] (así lo lee el scoring).
      if (Object.values(record).some(Array.isArray)) {
        const lines = Object.entries(record)
          .filter(([, itemIds]) => Array.isArray(itemIds) && itemIds.length > 0)
          .map(([categoryId, itemIds]) => {
            const category = game.categories.find((c) => c.id === categoryId);
            const texts = (itemIds as string[]).map((id) =>
              labelOf(items, id),
            );
            return `${category?.name ?? categoryId}: ${texts.join(", ")}`;
          });
        return lines.length > 0 ? lines.join("\n") : UNANSWERED;
      }

      // Shape legacy: itemId -> categoryId (o null si se dejó sin colocar).
      const entries = Object.entries(record as Record<string, string | null>);
      return entries
        .map(([itemId, categoryId]) => {
          const item = items.find((i) => i.id === itemId);
          const category = categoryId
            ? game.categories.find((c) => c.id === categoryId)
            : null;
          return `${item?.text ?? itemId} → ${
            category?.name ?? (categoryId ? categoryId : "Sin categoría")
          }`;
        })
        .join("\n");
    }

    case "matching": {
      // Map leftIndex/pairId -> rightId; ambos formatos aparecen en el wire.
      if (typeof answer !== "object" || Array.isArray(answer))
        return stringify(answer);
      const entries = Object.entries(answer as Record<string, string>);
      return entries
        .map(([leftKey, rightId]) => {
          const pair = game.pairs.find((p) => p.id === leftKey);
          const rightPair = game.pairs.find((p) => p.id === rightId);
          const leftLabel = pair?.left.text ?? leftKey;
          const rightLabel = rightPair?.right.text ?? rightId;
          return `${leftLabel} ↔ ${rightLabel}`;
        })
        .join("\n");
    }

    case "text_input":
    case "text_input_ai": {
      // Respuesta libre del alumno (string).
      return stringify(answer);
    }

    case "image_labeling": {
      // Map hotspotId -> labelId. La CLAVE es la zona y el VALOR la etiqueta
      // que el alumno soltó ahí: el scorer lee
      // `answer[label.correctHotspotId] === label.id` (score.ts:143), igual que
      // el frontend en producción. Los hotspots no tienen texto propio, así que
      // se numeran por su orden de autoría ("Zona N").
      if (typeof answer !== "object" || Array.isArray(answer))
        return stringify(answer);
      const zoneIndexOf = (hotspotId: string): number =>
        game.hotspots.findIndex((h) => h.id === hotspotId);
      // Orden por zona para que la salida no dependa del orden de claves con
      // que quedó serializado el JSON. Zonas desconocidas al final.
      const rank = (hotspotId: string): number => {
        const idx = zoneIndexOf(hotspotId);
        return idx < 0 ? Number.MAX_SAFE_INTEGER : idx;
      };
      return Object.entries(answer as Record<string, string | null>)
        .filter(([, labelId]) => !!labelId)
        .sort(([a], [b]) => rank(a) - rank(b))
        .map(([hotspotId, labelId]) => {
          const idx = zoneIndexOf(hotspotId);
          const zone = idx >= 0 ? `Zona ${idx + 1}` : hotspotId;
          const label = game.labels.find((l) => l.id === labelId);
          return `${zone} → ${label?.text ?? labelId}`;
        })
        .join("\n");
    }

    case "image_hotspot": {
      // Array de hotspotIds clickeados. Sin texto de autoría: se numeran por
      // orden y se indica si la zona era correcta.
      if (!Array.isArray(answer)) return stringify(answer);
      return (answer as string[])
        .map((id) => {
          const idx = game.hotspots.findIndex((h) => h.id === id);
          if (idx < 0) return id;
          const hotspot = game.hotspots[idx];
          return `Zona ${idx + 1} (${hotspot.correct ? "correcta" : "incorrecta"})`;
        })
        .join(", ");
    }

    case "text_selection": {
      // Array de part ids seleccionados.
      if (!Array.isArray(answer)) return stringify(answer);
      return (answer as string[])
        .map((id) => {
          const part = game.parts.find((p) => p.id === id);
          return part?.text ?? id;
        })
        .join(" · ");
    }

    default:
      return stringify(answer);
  }
};

export const UNANSWERED_LABEL = UNANSWERED;

/**
 * `score` son los puntos OBTENIDOS, en la misma unidad que `total_points`
 * (schema.prisma lo declara sobre la columna). No es una fracción 0..1.
 *
 * La versión anterior re-escalaba con `raw <= 1 ? raw * totalPoints : raw`,
 * heredado del contrato viejo en que el SDK sí emitía una fracción. Esa rama
 * mostraba un 1/10 real como "10 / 10": convertía al peor intento en perfecto,
 * justo en la vista que se usa para analizar cómo le fue a la clase. La
 * migración one-off 20260725_rescale_game_quiz_attempt_score.sql ya reescaló
 * las filas fraccionarias en los tres entornos, así que el puente sobra y solo
 * puede hacer daño de acá en adelante.
 */
export const formatScore = (
  score: number | null | undefined,
  totalPoints: number,
): string => {
  const points = score ?? 0;
  return `${Math.round(points * 10) / 10} / ${totalPoints}`;
};
